import { useState, useEffect, useCallback } from 'react';
import api, { API } from '../lib/api';
import { toast } from 'sonner';
import { Search, Download, Star, Users, AlertTriangle } from 'lucide-react';

const PAGE_SIZE = 100;

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso + 'T00:00:00').toLocaleDateString('de-DE', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return iso;
  }
}

export default function AdminGuests() {
  const [guests, setGuests] = useState([]);
  const [totalAll, setTotalAll] = useState(0);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('last_visit');
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/guests/list', { params: { search, sort } });
      setGuests(r.data?.guests || []);
      setTotalAll(r.data?.total_all || 0);
      setPage(1);
    } catch (e) {
      toast.error('Gästeliste konnte nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, [search, sort]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  const exportXlsx = async () => {
    setExporting(true);
    try {
      const resp = await api.get('/guests/export', { params: { search, sort }, responseType: 'blob' });
      const blob = new Blob([resp.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const stamp = new Date().toISOString().slice(0, 16).replace(/[-T:]/g, '');
      a.href = url;
      a.download = `gaesteliste_${stamp}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Gästeliste exportiert');
    } catch (e) {
      toast.error('Export fehlgeschlagen');
    } finally {
      setExporting(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(guests.length / PAGE_SIZE));
  const pageGuests = guests.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div className="p-6 lg:p-8 max-w-6xl mx-auto" data-testid="admin-guests">
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-wider text-[#5C5C59]">Stammgäste</p>
          <h1 className="font-display text-3xl text-[#1C1C19] flex items-center gap-2">
            <Users className="w-7 h-7" /> Gäste
          </h1>
          <p className="text-sm text-[#5C5C59] mt-1">{totalAll} bekannte Gäste (aus Reservierungen + importierten)</p>
        </div>
        <button
          onClick={exportXlsx}
          disabled={exporting || guests.length === 0}
          data-testid="guests-export-btn"
          className="inline-flex items-center gap-2 bg-[#1C1C19] text-white px-4 py-2.5 rounded text-sm hover:bg-[#3A3A35] transition disabled:opacity-50"
        >
          <Download className="w-4 h-4" /> {exporting ? 'Exportiere…' : 'Als Excel exportieren'}
        </button>
      </div>

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 text-[#9CA3AF] absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Name oder Telefon suchen…"
            data-testid="guests-search"
            className="w-full pl-9 pr-3 py-2.5 border border-[#E5E5E0] rounded text-sm focus:border-[#1C1C19] outline-none"
          />
        </div>
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          data-testid="guests-sort"
          className="px-3 py-2.5 border border-[#E5E5E0] rounded text-sm bg-white"
        >
          <option value="last_visit">Letzter Besuch</option>
          <option value="visits">Meiste Besuche</option>
          <option value="name">Name (A–Z)</option>
        </select>
      </div>

      <div className="bg-white border border-[#E5E5E0] rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="guests-table">
            <thead>
              <tr className="bg-[#F9F9F7] text-left text-xs uppercase tracking-wider text-[#5C5C59]">
                <th className="px-4 py-3 font-medium">Name</th>
                <th className="px-4 py-3 font-medium">Telefon</th>
                <th className="px-4 py-3 font-medium hidden md:table-cell">E-Mail</th>
                <th className="px-4 py-3 font-medium text-center">Besuche</th>
                <th className="px-4 py-3 font-medium text-center hidden sm:table-cell">No-Shows</th>
                <th className="px-4 py-3 font-medium hidden lg:table-cell">Letzter Besuch</th>
                <th className="px-4 py-3 font-medium text-center">Tisch</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-[#9CA3AF]">Lädt…</td></tr>
              ) : pageGuests.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-[#9CA3AF]" data-testid="guests-empty">Keine Gäste gefunden</td></tr>
              ) : (
                pageGuests.map((g, i) => (
                  <tr key={`${g.phone}-${g.name}-${i}`} className="border-t border-[#F0F0EC] hover:bg-[#F9F9F7]" data-testid="guest-row">
                    <td className="px-4 py-3">
                      <span className="font-medium text-[#1C1C19] inline-flex items-center gap-1.5">
                        {g.name || '—'}
                        {g.is_regular && <Star className="w-3.5 h-3.5 text-[#B45309] fill-current" title="Stammgast" />}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-[#5C5C59]">{g.phone || '—'}</td>
                    <td className="px-4 py-3 text-[#5C5C59] hidden md:table-cell">{g.email || '—'}</td>
                    <td className="px-4 py-3 text-center">{g.visits}</td>
                    <td className="px-4 py-3 text-center hidden sm:table-cell">
                      {g.no_shows > 0 ? (
                        <span className="inline-flex items-center gap-1 text-[#B91C1C]"><AlertTriangle className="w-3.5 h-3.5" />{g.no_shows}</span>
                      ) : <span className="text-[#D1D5DB]">0</span>}
                    </td>
                    <td className="px-4 py-3 text-[#5C5C59] hidden lg:table-cell">{fmtDate(g.last_visit)}</td>
                    <td className="px-4 py-3 text-center">
                      {g.favorite_table_number != null
                        ? <span className="inline-flex items-center gap-1 text-[#854D0E] bg-[#FEF9C3] border border-[#FDE68A] rounded-full px-2 py-0.5 text-xs"><Star className="w-3 h-3" />{g.favorite_table_number}</span>
                        : <span className="text-[#D1D5DB]">—</span>}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 text-sm" data-testid="guests-pagination">
          <span className="text-[#5C5C59]">Seite {page} von {totalPages} · {guests.length} Treffer</span>
          <div className="flex items-center gap-2">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="px-3 py-1.5 border border-[#E5E5E0] rounded disabled:opacity-40 hover:bg-[#F9F9F7]">Zurück</button>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="px-3 py-1.5 border border-[#E5E5E0] rounded disabled:opacity-40 hover:bg-[#F9F9F7]">Weiter</button>
          </div>
        </div>
      )}
    </div>
  );
}
